/* ------------------------------------------------------------------ */
/* UK Rental Yield Calculator — defaults and calculation logic         */
/* ------------------------------------------------------------------ */

export interface RentalYieldInputs {
  purchasePrice: number
  monthlyRent: number
  annualManagementFees: number
  annualMaintenance: number
  annualInsurance: number
  monthsVoid: number
  annualGroundRent: number
  monthlyMortgage: number
}

export interface RentalYieldResult {
  annualRent: number
  annualCosts: number
  grossYield: number
  netYield: number
  monthlyProfit: number
  annualProfit: number
  annualCashflow: number
}

export const RENTAL_YIELD_DEFAULTS: RentalYieldInputs = {
  purchasePrice: 250_000,
  monthlyRent: 1_200,
  annualManagementFees: 1_440, // ~10% of annual rent
  annualMaintenance: 1_000,
  annualInsurance: 300,
  monthsVoid: 1,
  annualGroundRent: 0,
  monthlyMortgage: 0,
} as const

export function calculateRentalYield(inputs: RentalYieldInputs): RentalYieldResult {
  const occupiedMonths = Math.max(12 - inputs.monthsVoid, 0)
  const annualRent = inputs.monthlyRent * occupiedMonths

  const annualCosts =
    inputs.annualManagementFees +
    inputs.annualMaintenance +
    inputs.annualInsurance +
    inputs.annualGroundRent

  const grossYield =
    inputs.purchasePrice > 0
      ? ((inputs.monthlyRent * 12) / inputs.purchasePrice) * 100
      : 0

  const netIncome = annualRent - annualCosts
  const netYield =
    inputs.purchasePrice > 0 ? (netIncome / inputs.purchasePrice) * 100 : 0

  const annualMortgage = inputs.monthlyMortgage * 12
  const annualCashflow = netIncome - annualMortgage
  const monthlyProfit = annualCashflow / 12
  const annualProfit = annualCashflow

  return {
    annualRent,
    annualCosts,
    grossYield: Math.round(grossYield * 100) / 100,
    netYield: Math.round(netYield * 100) / 100,
    monthlyProfit: Math.round(monthlyProfit),
    annualProfit: Math.round(annualProfit),
    annualCashflow: Math.round(annualCashflow),
  }
}

export { formatCurrency } from "@/lib/format-currency"
